import {BLOCKS, Document, helpers, Node} from '@contentful/rich-text-types'
import {Asset, AssetFields, EntrySkeletonType} from 'contentful'

import {ArticleQuestion} from '@/app/lib/structuredData'

import {
  Author,
  Blog,
  BlogOverview,
  ContentfulAuthor,
  ContentfulBlog,
  ContentfulCountryGuide,
  ContentfulIndustry,
  CountryGuideContent,
  Image,
  Industry,
} from './types'

export const parseImage = (asset: Asset): Image => {
  const assetFields = asset.fields as AssetFields

  return {
    url: 'https:' + assetFields.file?.url,
    alt: assetFields.title || '',
    width: assetFields.file?.details.image?.width || 0,
    height: assetFields.file?.details.image?.height || 0,
  }
}

const parseAuthors = (authors: EntrySkeletonType<ContentfulAuthor>[]): Author[] =>
  authors.map(({fields}) => ({
    ...fields,
    image: parseImage(fields.image),
  }))

const parseIndustry = (fields: ContentfulIndustry): Industry => ({
  ...fields,
  icon: parseImage(fields.icon),
  heroImage: parseImage(fields.heroImage),
})

const parseIndustryTopicName = (industryTopic: ContentfulBlog['industryTopic']) =>
  industryTopic ? industryTopic.fields.name : null

export const parseBlogs = (blogs: ContentfulBlog[]): Blog[] =>
  blogs.map((blog) => ({
    ...blog,
    image: parseImage(blog.image),
    authors: parseAuthors(blog.authors),
    //null is here beacause of getServerSideProps: https://github.com/vercel/next.js/discussions/11209
    industry: blog.industry?.fields ? parseIndustry(blog.industry.fields) : null,
    industryTopic: parseIndustryTopicName(blog.industryTopic),
    ogImage: blog.ogImage ? parseImage(blog.ogImage) : null,
  }))

export const parseBlogOverviews = (blogs: ContentfulBlog[]): BlogOverview[] =>
  blogs.map((blog) => ({
    title: blog.title,
    slug: blog.slug,
    readingTime: blog.readingTime,
    date: blog.date,
    blogIntro: blog.blogIntro,
    image: parseImage(blog.image),
    // An industry that is unpublished stays an unresolved link, with no `fields` to read.
    industry: blog.industry?.fields ? {name: blog.industry.fields.name} : null,
    industryTopic: parseIndustryTopicName(blog.industryTopic),
  }))

export const parseIndustries = (industries: ContentfulIndustry[]): Industry[] =>
  industries.map(parseIndustry)

export const parseCountryGuide = (guide: ContentfulCountryGuide): CountryGuideContent => ({
  ...guide,
  heroImage: guide.heroImage ? parseImage(guide.heroImage) : null,
  thumbnailImage: guide.thumbnailImage ? parseImage(guide.thumbnailImage) : null,
  ogImage: guide.ogImage ? parseImage(guide.ogImage) : null,
})

// Editors sometimes drop the scheme (`www.example.com`, `tramatm.com/…`) or leave a trailing `>`,
// which the browser would otherwise resolve relative to the current page.
const SCHEMELESS_HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(?=[/?#]|$)/i

export const normalizeContentfulHref = (uri: string): string => {
  const href = uri.trim().replace(/(>|%3E)$/i, '')
  return SCHEMELESS_HOST.test(href) ? `https://${href}` : href
}

// Blocks on separate lines, inline nodes (text, links) run together.
const toPlainText = (node: Node): string => {
  if (helpers.isText(node)) {
    return node.value
  }
  if (!('content' in node)) {
    return ''
  }
  const children = node.content as Node[]
  return children.map(toPlainText).join(children.some(helpers.isBlock) ? '\n' : '')
}

// A guide's h2s are mostly questions, answered by the blocks up to the next h2.
export const getGuideQuestions = (guide: Document): ArticleQuestion[] => {
  const questions: ArticleQuestion[] = []
  let current: ArticleQuestion | null = null

  for (const block of guide.content) {
    if (block.nodeType === BLOCKS.HEADING_2) {
      const name = toPlainText(block).trim()
      current = name.endsWith('?') ? {name, answer: ''} : null
      if (current) {
        questions.push(current)
      }
    } else if (current) {
      current.answer = `${current.answer}\n${toPlainText(block)}`.trim()
    }
  }

  return questions.filter(({answer}) => answer)
}
